/**
 * PC /chat Tab 入口参数桥接（方案 A：sessionStorage + pubsub）
 *
 * 背景：PC 端 Chat 以全页 Tab 呈现，无法像移动端那样通过 `useOpenChatModal` 直接传参。
 * 跨页跳转时 React 状态会丢失，因此用 sessionStorage 暂存 `IOnCreateParams` 子集，
 * 由 `ChatTabContent` mount 时消费；若用户已在 /chat（keep-alive / 同路由），则额外
 * 通过 pubsub 唤醒 init effect 重新消费。
 *
 * 数据流：
 *   openChatTabWithPendingEntry()
 *     → [autoRenew] 清 localStorage[CHAT_ID]
 *     → persistChatTabPendingEntry() 写 sessionStorage
 *     → notifyChatTabPendingEntry() 发 pubsub
 *     → router.push('/chat')
 *   ChatTabContent
 *     → consumeChatTabPendingEntry() 读并删
 *     → onCreateOrg() + autoSubmit 写 is_submit_chat
 *
 * @see ChatTabContent — 消费端
 * @see useResponsiveChatOpen — 大屏 Chat 入口；usePlanGoalChatOpen — Plan Goal 场景
 */
import { pubsub } from "utils";
import {
  LOCAL_STORAGE_KEYS,
  SESSION_STORAGE_KEYS,
} from "@/src/config/constant";
import { ROUTER_PATH } from "@/src/config/router";
import { pubSubKeyMap } from "@/src/utils/pubSubKeyMap";
import type { IOnCreateParams } from "@/src/hooks/useCreateChat";

/** 与 ChatModal / useCreateChat 对齐的待消费参数 */
export type ChatTabPendingEntry = Pick<
  IOnCreateParams,
  | "question"
  | "autoSubmit"
  | "autoRenew"
  | "cacheQuestion"
  | "sign"
  | "tpl"
  | "shareId"
  | "secondOpinion"
>;

export const hasChatTabPendingEntry = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return Boolean(
      sessionStorage.getItem(SESSION_STORAGE_KEYS.CHAT_TAB_PENDING_ENTRY),
    );
  } catch {
    return false;
  }
};

/** 写入 sessionStorage；须在 `router.push('/chat')` 之前调用 */
export const persistChatTabPendingEntry = (
  entry: ChatTabPendingEntry,
): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.CHAT_TAB_PENDING_ENTRY,
      JSON.stringify(entry),
    );
  } catch {
    /* ignore quota / private mode */
  }
};

/**
 * 读取并删除 pending entry（一次性消费）。
 * 无效 payload（无 question 且无 shareId）会清 key 并返回 null。
 */
export const consumeChatTabPendingEntry = (): ChatTabPendingEntry | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = sessionStorage.getItem(
      SESSION_STORAGE_KEYS.CHAT_TAB_PENDING_ENTRY,
    );
    if (!raw) {
      return null;
    }
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.CHAT_TAB_PENDING_ENTRY);
    const parsed = JSON.parse(raw) as ChatTabPendingEntry;
    if (!parsed?.question && !parsed?.shareId && !parsed?.secondOpinion) {
      return null;
    }
    return parsed;
  } catch {
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.CHAT_TAB_PENDING_ENTRY);
    return null;
  }
};

/**
 * 通知已挂载的 ChatTabContent 重新跑 init。
 * 场景：用户已在 /chat，再次从 Plan 等页触发入口时路由不会 remount。
 */
export const notifyChatTabPendingEntry = (): void => {
  pubsub.emit(pubSubKeyMap.CHAT_TAB_PENDING_ENTRY);
};

type ChatTabRouter = {
  push: (href: string) => void;
};

/**
 * PC 统一入口：暂存参数 → pubsub 唤醒 → 跳转 /chat。
 * `autoRenew` 时清 CHAT_ID，与 `useOpenChatModal` 行为一致，避免续聊旧会话。
 */
export const openChatTabWithPendingEntry = (
  router: ChatTabRouter,
  entry: ChatTabPendingEntry,
): void => {
  if (typeof window !== "undefined" && entry.autoRenew) {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.CHAT_ID);
  }

  // 先写 storage 再 emit，保证 ChatTabContent 消费时数据已就绪
  persistChatTabPendingEntry(entry);
  notifyChatTabPendingEntry();
  router.push(ROUTER_PATH.CHAT);
};
